// src/lib/morningMeetingShape.js
// STATUS: active | UPDATED: 2026-09-23
// LOG:
//   2026-09-23  New. Split from morningMeeting.js so it can be tested.
//
// THE SHAPE OF HIS MORNING MEETING.
//
// One line in his daily disciplines: "Morning meeting with results,
// appointments, deliveries, aged opportunities and one skill focus." Five
// things, and the fifth is singular.
//
// Everything here is a pure function of its arguments. Keep it that way.

// His five, in his order. The prompts are ours and they exist because a field
// labelled "Results" with nothing under it gets "good month" typed into it.
export const AGENDA = [
  {
    key: 'results',
    label: 'Results',
    prompt: 'Where the month actually is. Yesterday and month to date.',
  },
  {
    key: 'appointments',
    label: 'Appointments',
    prompt: 'What is on the board today, and which ones are confirmed.',
  },
  {
    key: 'deliveries',
    label: 'Deliveries',
    prompt: 'What is going out today and what has to be ready for it.',
  },
  {
    key: 'aged',
    label: 'Aged opportunities',
    prompt: 'What has been sitting too long. Counts and themes, never customer names.',
  },
]

// The fifth is separate because it is the only one with a rule attached.
export const FOCUS = {
  key: 'skill_focus',
  label: 'One skill focus',
  prompt:
    'One. Not three. Ninety seconds on a single thing everybody works on today, and it is the reason this meeting is training rather than a briefing.',
}

const filled = (v) => String(v || '').trim().length > 0

// PREPARED IS THE INTERESTING STATE AND IT IS NOT THE SAME AS HELD.
//
// The complaint about dealership morning meetings is never that they do not
// happen. It is that nobody prepared: the meeting is held because it is
// Tuesday, and whoever runs it writes two things on a pad while everybody sits
// down. So this product can tell the difference, and the difference is the
// whole reason the artefact exists.
export function meetingState(row) {
  if (!row) return 'none'
  const prepared = AGENDA.some((f) => filled(row[f.key])) || filled(row[FOCUS.key])
  if (row.held_at && prepared) return 'held'
  if (row.held_at) return 'unprepared'
  if (prepared) return 'prepared'
  return 'empty'
}

export function agendaProgress(row) {
  const done = AGENDA.filter((f) => filled(row?.[f.key])).length + (filled(row?.[FOCUS.key]) ? 1 : 0)
  return { done, total: AGENDA.length + 1 }
}

// The one rule he stated with a number in it. A manager who lists four things
// has not chosen, and choosing is the discipline.
//
// Counts separators rather than words: "follow up and the trade walk and
// prospecting" is three, and so is a comma separated list. Warns, never blocks,
// like the vague feedback check on the one on one.
export function tooManyFocuses(text) {
  const said = String(text || '').trim()
  if (!said) return false
  const parts = said
    .split(/,| and | plus |;|\/|\n/i)
    .map((p) => p.trim())
    .filter((p) => p.length > 2)
  return parts.length > 2
}

// A step number, when the focus is one of the thirteen. Null is fine: not
// everything worth ninety seconds on a Tuesday is one of his steps.
export function stepOk(n) {
  if (n == null || n === '') return true
  const v = Number(n)
  return Number.isInteger(v) && v >= 1 && v <= 13
}

// How many of the last `days` had a meeting held. His daily audit asks "morning
// meeting completed" as a yes or no, and a run of days is the thing a yes or no
// cannot show.
export function heldStreak(rows, days, today) {
  const held = new Set((rows || []).filter((r) => r.held_at).map((r) => r.meeting_on))
  let count = 0
  for (const day of days) {
    if (held.has(day)) count += 1
  }
  return { held: count, of: days.length, today: held.has(today) }
}
